import { useState, useEffect, type MouseEvent } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'


type Theme = 'light' | 'dark'

// ── CSS ──────────────────────────────────────────────────────────────────────
const LP_CSS = `
@import url('https://cdn.jsdelivr.net/fontsource/css/inter@latest/400.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/inter@latest/500.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/inter@latest/600.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/inter-tight@latest/700.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/inter-tight@latest/800.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/jetbrains-mono@latest/400.css');
@import url('https://cdn.jsdelivr.net/fontsource/css/jetbrains-mono@latest/500.css');

#lp-root{--ink:#0f1016;--muted:#636672;--faint:#9a9caa;--line:#e8e9f0;--bg:#f7f7fa;--paper:#ffffff;--accent:#6366f1;--accent-2:#4f46e5;--soft:#eef0ff;--navy:#12142e;--fb:'Inter',-apple-system,'Segoe UI',sans-serif;--fd:'Inter Tight',-apple-system,'Segoe UI',sans-serif;--fm:'JetBrains Mono',ui-monospace,'SF Mono',monospace;--r:16px}
#lp-root[data-theme="dark"]{--ink:#e8eaf6;--muted:#888ba8;--faint:#454660;--line:#252640;--bg:#111220;--paper:#18192c;--accent:#6366f1;--accent-2:#818cf8;--soft:#1c1e40;--navy:#0b0c1a}

#lp-root,#lp-root *{transition:background-color .4s ease,color .4s ease,border-color .4s ease,fill .4s ease,box-shadow .4s ease}
@media (prefers-reduced-motion:reduce){#lp-root,#lp-root *{transition:none}}

#lp-root *{margin:0;padding:0;box-sizing:border-box}
#lp-root{font-family:var(--fb);font-size:15px;line-height:1.6;color:var(--ink);background:var(--paper);-webkit-font-smoothing:antialiased;min-height:100vh}
#lp-root ::selection{background:var(--accent);color:#fff}
#lp-root a{text-decoration:none;color:inherit}
#lp-root ul{list-style:none}
#lp-root button{font-family:inherit;border:0;background:none;cursor:pointer;color:inherit}
#lp-root :focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:6px}
#lp-root .container{max-width:1160px;margin:0 auto;padding:0 24px}
#lp-root .section{padding:110px 0}
#lp-root .tinted{background:var(--bg)}
#lp-root .kicker{font-family:var(--fm);font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;color:var(--accent)}
#lp-root .sec-head{text-align:center;max-width:680px;margin:0 auto 64px}
#lp-root .sec-head .title{margin-top:16px}
#lp-root .title{font-family:var(--fd);font-weight:700;letter-spacing:-.035em;line-height:1.06;font-size:clamp(32px,5vw,52px);color:var(--ink)}
#lp-root .sub{color:var(--muted);font-size:15.5px;margin-top:16px}
#lp-root .acc{color:var(--accent)}

#lp-root .reveal{opacity:0;transform:translateY(22px);transition:opacity .8s cubic-bezier(.2,.6,.2,1),transform .8s cubic-bezier(.2,.6,.2,1);transition-delay:var(--d,0s)}
#lp-root .reveal.in{opacity:1;transform:none}

#lp-root .btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;font-weight:600;font-size:14.5px;padding:13px 24px;border-radius:12px;transition:all .25s ease;white-space:nowrap;cursor:pointer;border:0;font-family:var(--fb)}
#lp-root .btn .arr{transition:transform .25s}
#lp-root .btn:hover .arr{transform:translateX(3px)}
#lp-root .btn:active{transform:scale(.98)}
#lp-root .btn-primary{background:var(--accent);color:#fff;box-shadow:0 10px 28px rgba(99,102,241,.32)}
#lp-root .btn-primary:hover{background:var(--accent-2);transform:translateY(-2px)}
#lp-root .btn-ghost{background:var(--paper);border:1px solid var(--line);color:var(--ink)}
#lp-root .btn-ghost:hover{border-color:#c9cbdb;transform:translateY(-2px)}
#lp-root[data-theme="dark"] .btn-ghost:hover{border-color:#3a3c60}
#lp-root .btn-white{background:#fff;color:var(--accent-2)}
#lp-root .btn-white:hover{transform:translateY(-2px);box-shadow:0 12px 30px rgba(10,10,40,.25)}
#lp-root .btn-outline-w{border:1px solid var(--line);color:var(--ink);background:var(--paper)}
#lp-root .btn-outline-w:hover{border-color:#c9cbdb;transform:translateY(-2px)}
#lp-root[data-theme="dark"] .btn-outline-w:hover{border-color:#3a3c60}
#lp-root .btn-sm{padding:9px 16px;border-radius:10px;font-size:13.5px}
#lp-root .btn-block{width:100%}

#lp-root .theme-toggle{display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:10px;border:1px solid var(--line);color:var(--muted);background:var(--paper);transition:all .2s;flex-shrink:0}
#lp-root .theme-toggle:hover{border-color:var(--accent);color:var(--accent)}

#lp-root header{position:sticky;top:0;z-index:60;background:rgba(255,255,255,.78);-webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);border-bottom:1px solid transparent;transition:border-color .3s,box-shadow .3s,background .3s}
#lp-root header.scrolled{border-color:var(--line);box-shadow:0 6px 24px rgba(15,16,40,.05)}
#lp-root[data-theme="dark"] header{background:rgba(24,25,44,.82)}
#lp-root[data-theme="dark"] header.scrolled{box-shadow:0 6px 24px rgba(0,0,0,.3)}
#lp-root .header-in{display:flex;align-items:center;gap:16px;height:68px}
#lp-root .brand{display:flex;align-items:center;gap:9px}
#lp-root .brand-mark{width:28px;height:28px;border-radius:9px;background:var(--accent);color:#fff;font-weight:800;font-size:13px;display:grid;place-items:center;box-shadow:0 6px 16px rgba(99,102,241,.35)}
#lp-root .brand-name{font-family:var(--fd);font-weight:800;font-size:16px;letter-spacing:-.02em}
#lp-root .brand-beta{font-family:var(--fm);font-size:9.5px;letter-spacing:.08em;color:var(--accent-2);border:1px solid rgba(99,102,241,.3);background:var(--soft);border-radius:999px;padding:2px 8px}
#lp-root .nav{display:flex;gap:26px;margin-inline:auto}
#lp-root .nav a{font-size:13.5px;font-weight:500;color:var(--muted);transition:color .2s}
#lp-root .nav a:hover{color:var(--ink)}
#lp-root .header-cta{display:flex;align-items:center;gap:10px}
#lp-root .link-login{font-size:13.5px;font-weight:500;color:var(--muted);transition:color .2s;padding:0;background:none;border:0;cursor:pointer;font-family:var(--fb)}
#lp-root .link-login:hover{color:var(--ink)}
#lp-root .burger{display:none;flex-direction:column;gap:5px;padding:6px;background:none;border:0;cursor:pointer}
#lp-root .burger span{display:block;width:20px;height:2px;background:var(--ink);border-radius:2px;transition:transform .3s,opacity .3s}
#lp-root .mobile-menu{position:absolute;top:68px;left:0;right:0;background:var(--paper);border-bottom:1px solid var(--line);padding:14px 24px 22px;display:flex;flex-direction:column;z-index:59}
#lp-root .mobile-menu a,#lp-root .mobile-menu .link-login{padding:11px 2px;font-size:15px;font-weight:600;border-bottom:1px solid var(--bg);text-align:left;display:block}
#lp-root .mobile-menu .btn{margin-top:16px}

#lp-root .hero{position:relative;padding:140px 0 96px;overflow:hidden}
#lp-root .hero::before{content:"";position:absolute;inset:0;background-image:radial-gradient(#e2e4f0 1px,transparent 1.4px);background-size:22px 22px;-webkit-mask-image:linear-gradient(#000 5%,transparent 78%);mask-image:linear-gradient(#000 5%,transparent 78%);pointer-events:none}
#lp-root[data-theme="dark"] .hero::before{background-image:radial-gradient(#252640 1px,transparent 1.4px)}
#lp-root .hero::after{content:"";position:absolute;inset:0;background:radial-gradient(720px 340px at 50% -60px,rgba(99,102,241,.10),transparent 70%);pointer-events:none}
#lp-root[data-theme="dark"] .hero::after{background:radial-gradient(720px 340px at 50% -60px,rgba(99,102,241,.18),transparent 70%)}
#lp-root .hero .container{position:relative;z-index:1}
#lp-root .hero-head{text-align:center;max-width:820px;margin:0 auto}
#lp-root .badge{display:inline-flex;align-items:center;gap:9px;font-family:var(--fm);font-size:11px;letter-spacing:.1em;color:var(--accent-2);background:var(--paper);border:1px solid var(--line);border-radius:999px;padding:8px 16px;box-shadow:0 4px 14px rgba(15,16,40,.05)}
#lp-root[data-theme="dark"] .badge{box-shadow:0 4px 14px rgba(0,0,0,.25)}
#lp-root .badge .dot{width:6px;height:6px;border-radius:50%;background:var(--accent);animation:lp-pulse 2s infinite}
@keyframes lp-pulse{0%{box-shadow:0 0 0 0 rgba(99,102,241,.45)}100%{box-shadow:0 0 0 9px rgba(99,102,241,0)}}
#lp-root .hero h1{font-family:var(--fd);font-weight:800;letter-spacing:-.04em;line-height:1.02;font-size:clamp(42px,7.4vw,88px);margin:28px 0 20px;color:var(--ink)}
#lp-root .lead{color:var(--muted);font-size:clamp(15px,2vw,17.5px);max-width:600px;margin:0 auto}
#lp-root .hero-cta{display:flex;gap:14px;justify-content:center;margin-top:36px;flex-wrap:wrap}
#lp-root .play-ic{width:22px;height:22px;border-radius:50%;background:var(--soft);color:var(--accent-2);display:grid;place-items:center}
#lp-root .micro{font-family:var(--fm);font-size:11px;letter-spacing:.1em;color:var(--faint);margin-top:26px}

#lp-root .window{margin-top:76px;background:var(--paper);border:1px solid var(--line);border-radius:18px;overflow:hidden;box-shadow:0 40px 100px rgba(15,16,45,.14)}
#lp-root[data-theme="dark"] .window{box-shadow:0 40px 100px rgba(0,0,0,.55)}
#lp-root .win-bar{display:flex;align-items:center;gap:7px;padding:12px 16px;border-bottom:1px solid var(--line);background:var(--bg)}
#lp-root[data-theme="dark"] .win-bar{background:#141526}
#lp-root .wdot{width:10px;height:10px;border-radius:50%}
#lp-root .wdot.r{background:#ff5f57}#lp-root .wdot.y{background:#febc2e}#lp-root .wdot.g{background:#28c840}
#lp-root .win-url{margin:0 auto;font-family:var(--fm);font-size:10.5px;color:var(--faint);background:var(--paper);border:1px solid var(--line);border-radius:8px;padding:4px 40px}
#lp-root .app-bar{display:flex;align-items:center;gap:12px;padding:10px 16px;border-bottom:1px solid var(--line)}
#lp-root .app-logo{width:22px;height:22px;border-radius:7px;background:var(--accent);color:#fff;font-size:10px;font-weight:800;display:grid;place-items:center}
#lp-root .app-name{font-weight:800;font-size:13px;color:var(--ink)}
#lp-root .app-beta{font-family:var(--fm);font-size:8.5px;color:var(--accent-2);border:1px solid var(--line);border-radius:999px;padding:1px 7px}
#lp-root .app-tabs{display:flex;gap:4px;margin-left:14px}
#lp-root .atab{font-size:11.5px;font-weight:500;color:var(--muted);padding:6px 11px;border-radius:8px;cursor:default}
#lp-root .atab.active{background:var(--soft);color:var(--accent-2);font-weight:600}
#lp-root .app-right{margin-left:auto;display:flex;align-items:center;gap:10px}
#lp-root .zoom{font-family:var(--fm);font-size:10px;color:var(--faint)}
#lp-root .mini-btn{font-size:11px;font-weight:600;padding:6px 12px;border-radius:8px;border:1px solid var(--line);color:var(--muted);background:var(--paper);cursor:default}
#lp-root .mini-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;box-shadow:0 5px 14px rgba(99,102,241,.35)}
#lp-root .app-body{display:grid;grid-template-columns:196px 1fr 216px;min-height:400px}
#lp-root .side{padding:14px;background:var(--bg)}
#lp-root[data-theme="dark"] .side{background:#141526}
#lp-root .side.left{border-right:1px solid var(--line)}
#lp-root .side.right{border-left:1px solid var(--line)}
#lp-root .side-tabs{display:flex;gap:4px;margin-bottom:12px}
#lp-root .stab{font-size:11px;font-weight:600;color:var(--muted);padding:5px 10px;border-radius:7px;cursor:default}
#lp-root .stab.active{background:var(--soft);color:var(--accent-2)}
#lp-root .side-label{font-family:var(--fm);font-size:8.5px;letter-spacing:.16em;text-transform:uppercase;color:var(--faint);margin:12px 4px 7px}
#lp-root .b-item{display:flex;align-items:center;gap:9px;font-size:11.5px;color:var(--muted);padding:7px 8px;border-radius:8px;transition:background .2s;cursor:default}
#lp-root .b-item:hover{background:var(--soft)}
#lp-root .b-item svg{color:var(--faint);flex:none}
#lp-root .canvas{background:#f4f5fa;background-image:radial-gradient(#dddfec 1px,transparent 1.2px);background-size:16px 16px;padding:38px 26px 26px;display:flex;justify-content:center}
#lp-root[data-theme="dark"] .canvas{background:#0e0f1e;background-image:radial-gradient(#1c1e38 1px,transparent 1.2px)}
#lp-root .frame{position:relative;width:100%;max-width:560px;background:var(--paper);border:1.5px solid var(--accent);border-radius:12px;box-shadow:0 16px 44px rgba(18,20,60,.10)}
#lp-root[data-theme="dark"] .frame{box-shadow:0 16px 44px rgba(0,0,0,.45)}
#lp-root .frame-tag{position:absolute;top:-26px;left:0;font-family:var(--fm);font-size:9px;letter-spacing:.06em;background:var(--accent);color:#fff;border-radius:6px;padding:3px 9px}
#lp-root .handle{position:absolute;width:8px;height:8px;background:var(--paper);border:1.5px solid var(--accent);border-radius:2px}
#lp-root .handle.tl{top:-5px;left:-5px}#lp-root .handle.tr{top:-5px;right:-5px}#lp-root .handle.bl{bottom:-5px;left:-5px}#lp-root .handle.br{bottom:-5px;right:-5px}
#lp-root .f-hero{background:linear-gradient(180deg,#181b3e,#101228);border-radius:10px 10px 0 0;padding:30px 22px;text-align:center}
#lp-root .f-pill{display:inline-block;font-family:var(--fm);font-size:8px;letter-spacing:.1em;color:rgba(255,255,255,.75);border:1px solid rgba(255,255,255,.25);border-radius:999px;padding:3px 10px;margin-bottom:12px}
#lp-root .f-hero b{display:block;color:#fff;font-size:15px;font-weight:700;letter-spacing:-.01em;line-height:1.3}
#lp-root .f-hero i{display:block;font-style:normal;color:rgba(255,255,255,.55);font-size:9.5px;margin-top:7px}
#lp-root .f-btns{display:flex;gap:8px;justify-content:center;margin-top:14px}
#lp-root .f-btn{font-size:9px;font-weight:600;border-radius:7px;padding:6px 12px;cursor:default}
#lp-root .f-btn.a{background:var(--accent);color:#fff}
#lp-root .f-btn.g{border:1px solid rgba(255,255,255,.3);color:#fff;background:none}
#lp-root .f-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;padding:12px}
#lp-root .f-card{border:1px solid var(--line);border-radius:9px;padding:12px 10px;text-align:center;background:var(--paper)}
#lp-root .f-dot{width:10px;height:10px;border-radius:3.5px;display:inline-block;margin-bottom:6px}
#lp-root .f-card b{display:block;font-size:9.5px;font-weight:700;color:var(--ink)}
#lp-root .f-card i{display:block;font-style:normal;font-size:8px;color:var(--faint);margin-top:3px}
#lp-root .prop{margin-bottom:11px}
#lp-root .field{border:1px solid var(--line);border-radius:8px;background:var(--paper);font-size:10px;color:var(--muted);padding:7px 9px}
#lp-root .bg-row{display:flex;align-items:center;gap:8px}
#lp-root .swatch{width:18px;height:18px;border-radius:6px;background:linear-gradient(135deg,#181b3e,var(--accent));flex:none}
#lp-root .align-seg{display:flex;border:1px solid var(--line);border-radius:8px;overflow:hidden}
#lp-root .align-seg span{flex:1;display:grid;place-items:center;padding:6px 0;color:var(--faint);cursor:default}
#lp-root .align-seg span.active{background:var(--soft);color:var(--accent-2)}
#lp-root .accent-fill{background:var(--accent)}

#lp-root .stats-band{border-block:1px solid var(--line);background:var(--paper)}
#lp-root .stats{display:grid;grid-template-columns:repeat(4,1fr)}
#lp-root .stat{padding:38px 20px;text-align:center;border-left:1px solid var(--line)}
#lp-root .stat:first-child{border-left:0}
#lp-root .stat-num{display:block;font-family:var(--fd);font-weight:800;font-size:clamp(28px,3.4vw,38px);letter-spacing:-.03em;color:var(--ink)}
#lp-root .stat-lbl{font-size:12.5px;color:var(--muted);margin-top:4px}

#lp-root .fgrid{display:grid;grid-template-columns:repeat(4,1fr);column-gap:36px}
#lp-root .fcell{padding:28px 4px 36px;border-top:1px solid var(--line)}
#lp-root .f-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px}
#lp-root .f-ico{width:36px;height:36px;border-radius:11px;background:var(--soft);color:var(--accent);display:grid;place-items:center;transition:all .3s}
#lp-root .fcell:hover .f-ico{background:var(--accent);color:#fff;transform:rotate(-6deg)}
#lp-root .f-num{font-family:var(--fm);font-size:11px;color:var(--faint)}
#lp-root .fcell h3{font-size:15.5px;font-weight:600;letter-spacing:-.01em;color:var(--ink)}
#lp-root .fcell p{font-size:13.5px;color:var(--muted);line-height:1.65;margin-top:9px}

#lp-root .marquee{border-block:1px solid var(--line);padding:17px 0;overflow:hidden;background:var(--paper)}
#lp-root .mq-track{display:flex;width:max-content;animation:lp-mq 40s linear infinite}
#lp-root .marquee:hover .mq-track{animation-play-state:paused}
#lp-root .mq-group{display:flex}
#lp-root .mq-item{display:flex;align-items:center;font-family:var(--fm);font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--faint);padding:0 26px;white-space:nowrap}
#lp-root .mq-item::before{content:"";width:4px;height:4px;border-radius:50%;background:var(--accent);opacity:.5;margin-right:52px}
@keyframes lp-mq{to{transform:translateX(-50%)}}

#lp-root .steps{display:grid;grid-template-columns:repeat(3,1fr);gap:44px;position:relative;margin-top:64px}
#lp-root .steps::before{content:"";position:absolute;top:24px;left:14%;right:14%;height:1px;background:var(--line)}
#lp-root .step{text-align:center;position:relative}
#lp-root .step-num{width:48px;height:48px;border-radius:50%;background:var(--paper);border:1px solid var(--line);color:var(--accent);font-family:var(--fm);font-size:13px;font-weight:500;display:grid;place-items:center;margin:0 auto 24px;position:relative;z-index:1;transition:all .3s;box-shadow:0 5px 16px rgba(15,16,40,.06)}
#lp-root[data-theme="dark"] .step-num{box-shadow:0 5px 16px rgba(0,0,0,.3)}
#lp-root .step:hover .step-num{background:var(--accent);color:#fff;border-color:var(--accent)}
#lp-root .step h3{font-size:16.5px;font-weight:700;letter-spacing:-.01em;color:var(--ink)}
#lp-root .step p{font-size:13.5px;color:var(--muted);max-width:270px;margin:10px auto 0;line-height:1.65}

#lp-root .tgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
#lp-root .tcard{border:1px solid var(--line);border-radius:var(--r);background:var(--paper);overflow:hidden;transition:transform .35s,box-shadow .35s,border-color .35s;display:block;cursor:pointer}
#lp-root .tcard:hover{transform:translateY(-6px);box-shadow:0 24px 56px rgba(15,16,45,.09);border-color:#d3d5e6}
#lp-root[data-theme="dark"] .tcard:hover{box-shadow:0 24px 56px rgba(0,0,0,.45);border-color:#3a3c60}
#lp-root .thumb{height:196px;background:var(--thumb-bg,var(--bg));border-bottom:1px solid var(--line);padding:15px;overflow:hidden}
#lp-root .thumb i{display:block;background:var(--thumb-block,var(--line));border-radius:4px}
#lp-root .tw-nav{display:flex;align-items:center;gap:8px;margin-bottom:14px}
#lp-root .tw-logo{width:12px;height:12px;border-radius:4px;background:var(--accent);flex:none}
#lp-root .tw-btn{width:26px;height:10px;border-radius:5px;background:var(--line)}
#lp-root .tw-btn.ab{background:var(--accent)}
#lp-root .tw-bar{height:6px;border-radius:4px;background:var(--line)}
#lp-root .tw-bar.ab{background:var(--accent)}
#lp-root .w40{width:40%}#lp-root .w45{width:45%}#lp-root .w55{width:55%}#lp-root .w60{width:60%}#lp-root .w70{width:70%}#lp-root .w80{width:80%}
#lp-root .h5{height:5px}#lp-root .h8{height:8px}#lp-root .h10{height:10px}
#lp-root .tw-hero-c{display:flex;flex-direction:column;align-items:center;gap:7px;padding:8px 0 14px}
#lp-root .tw-cta{display:flex;gap:6px;margin-top:5px}
#lp-root .tw-cards3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
#lp-root .tw-cards3 i{height:36px;border-radius:7px}
#lp-root .tw-grid4{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
#lp-root .tw-prod{display:flex;flex-direction:column;gap:5px}
#lp-root .tw-prod .img{height:52px;border-radius:7px;background:var(--line)}
#lp-root .tw-prod .ln{height:4px;width:70%;border-radius:4px;background:var(--line)}
#lp-root .tw-port{display:flex;gap:12px}
#lp-root .tw-left{flex:1;display:flex;flex-direction:column;gap:8px;padding-top:4px}
#lp-root .tw-img{width:44%;border-radius:9px;background:linear-gradient(135deg,#e4e7fb,#b9bef2)}
#lp-root[data-theme="dark"] .tw-img{background:linear-gradient(135deg,#2a2d58,#1e2040)}
#lp-root .tw-rows{display:flex;flex-direction:column;gap:10px}
#lp-root .tw-row{display:flex;gap:10px;align-items:center}
#lp-root .tw-row .sq{width:34px;height:34px;border-radius:8px;flex:none;background:var(--line)}
#lp-root .tw-row .col{flex:1;display:flex;flex-direction:column;gap:5px}
#lp-root .tw-row .ln1{height:6px;width:80%;border-radius:4px;background:var(--line)}
#lp-root .tw-row .ln2{height:4px;width:50%;border-radius:4px;background:var(--line)}
#lp-root .t-info{display:flex;align-items:center;justify-content:space-between;padding:14px 16px}
#lp-root .t-name{font-size:14px;font-weight:600;color:var(--ink)}
#lp-root .t-tag{font-family:var(--fm);font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint)}
#lp-root .t-arrow{color:var(--accent);opacity:0;transform:translateX(-5px);transition:all .3s}
#lp-root .tcard:hover .t-arrow{opacity:1;transform:none}
#lp-root .t-more{text-align:center;margin-top:44px}

#lp-root .rgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
#lp-root .rcard{border:1px solid var(--line);border-radius:var(--r);background:var(--paper);padding:28px;display:flex;flex-direction:column;gap:20px;transition:transform .35s,box-shadow .35s}
#lp-root .rcard:hover{transform:translateY(-5px);box-shadow:0 22px 52px rgba(15,16,45,.07)}
#lp-root[data-theme="dark"] .rcard:hover{box-shadow:0 22px 52px rgba(0,0,0,.4)}
#lp-root .stars{display:flex;gap:4px;color:var(--accent)}
#lp-root .quote{font-size:14px;line-height:1.7;color:var(--ink);flex:1}
#lp-root .r-foot{display:flex;align-items:center;gap:12px}
#lp-root .avatar{width:38px;height:38px;border-radius:50%;background:var(--soft);color:var(--accent-2);font-size:12px;font-weight:700;display:grid;place-items:center;flex:none}
#lp-root .r-name{font-size:13.5px;font-weight:700;color:var(--ink)}
#lp-root .r-role{font-size:11.5px;color:var(--muted)}

#lp-root .pgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;align-items:stretch;margin-top:70px}
#lp-root .pcard{position:relative;border:1px solid var(--line);border-radius:20px;background:var(--paper);padding:32px;display:flex;flex-direction:column;transition:transform .35s,box-shadow .35s}
#lp-root .pcard:hover{transform:translateY(-5px);box-shadow:0 22px 52px rgba(15,16,45,.07)}
#lp-root[data-theme="dark"] .pcard:hover{box-shadow:0 22px 52px rgba(0,0,0,.35)}
#lp-root .p-name{font-family:var(--fm);font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
#lp-root .p-price{font-family:var(--fd);font-weight:800;font-size:42px;letter-spacing:-.03em;margin:16px 0 2px;color:var(--ink)}
#lp-root .per{font-family:var(--fb);font-size:12.5px;font-weight:500;color:var(--muted);letter-spacing:0}
#lp-root .p-desc{font-size:13px;color:var(--muted);margin-top:6px}
#lp-root .p-list{display:grid;gap:12px;margin:26px 0 30px}
#lp-root .p-list li{display:flex;align-items:flex-start;gap:10px;font-size:13.5px;color:var(--ink)}
#lp-root .p-list svg{flex:none;margin-top:2px;color:var(--accent)}
#lp-root .p-cta{margin-top:auto}
#lp-root .pcard.pro{background:var(--accent);border-color:var(--accent);color:#fff;transform:translateY(-16px);box-shadow:0 32px 70px rgba(99,102,241,.35)}
#lp-root .pcard.pro:hover{transform:translateY(-21px)}
#lp-root .pcard.pro .p-name,#lp-root .pcard.pro .per,#lp-root .pcard.pro .p-desc{color:rgba(255,255,255,.72)}
#lp-root .pcard.pro .p-price{color:#fff}
#lp-root .pcard.pro .p-list li{color:#fff}
#lp-root .pcard.pro .p-list svg{color:#fff}
#lp-root .p-badge{position:absolute;top:-13px;left:50%;transform:translateX(-50%);background:#10111c;color:#fff;font-family:var(--fm);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;border-radius:999px;padding:6px 15px;white-space:nowrap}
#lp-root .p-note{text-align:center;font-size:12.5px;color:var(--muted);margin-top:48px}
#lp-root .pgrid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;align-items:stretch;margin-top:48px}
@media(max-width:900px){#lp-root .pgrid-4{grid-template-columns:repeat(2,1fr)}}
@media(max-width:520px){#lp-root .pgrid-4{grid-template-columns:1fr}}
#lp-root .ptabs{display:flex;justify-content:center;gap:0;margin:36px auto 0;background:var(--bg);border:1px solid var(--line);border-radius:12px;padding:4px;width:fit-content}
#lp-root .ptab-btn{padding:8px 28px;border:none;background:none;border-radius:9px;font-family:var(--fb);font-size:14px;font-weight:600;color:var(--muted);cursor:pointer;transition:background .18s,color .18s}
#lp-root .ptab-btn.active{background:var(--paper);color:var(--ink);box-shadow:0 1px 4px rgba(0,0,0,.08)}
#lp-root .billing-toggle{display:flex;justify-content:center;align-items:center;gap:12px;margin:28px 0 0;font-size:13px;color:var(--muted)}
#lp-root .btoggle{display:flex;background:var(--bg);border:1px solid var(--line);border-radius:999px;padding:3px;gap:2px}
#lp-root .btoggle-btn{padding:5px 16px;border:none;border-radius:999px;font-size:12.5px;font-weight:600;cursor:pointer;transition:background .15s,color .15s;background:none;color:var(--muted)}
#lp-root .btoggle-btn.active{background:var(--paper);color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.08)}
#lp-root .save-badge{background:#dcfce7;color:#16a34a;border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700}
#lp-root[data-theme="dark"] .save-badge{background:rgba(22,163,74,.18);color:#4ade80}
#lp-root .pcard.host-featured{background:var(--accent);border-color:var(--accent);color:#fff}
#lp-root .pcard.host-featured .p-name,#lp-root .pcard.host-featured .per,#lp-root .pcard.host-featured .p-desc{color:rgba(255,255,255,.72)}
#lp-root .pcard.host-featured .p-price{color:#fff}
#lp-root .pcard.host-featured .p-list li{color:#fff}
#lp-root .pcard.host-featured .p-list svg{color:#fff}
#lp-root .pcard.host-featured .p-badge{background:#10111c}

#lp-root .early{background:var(--bg)}
#lp-root .early .sec-head{margin-bottom:0}
#lp-root .wl-form{max-width:660px;margin:48px auto 0;display:grid;grid-template-columns:1fr 1.25fr;gap:12px}
#lp-root .lp-input{width:100%;padding:13px 17px;border:1px solid var(--line);border-radius:12px;background:var(--paper);font-family:var(--fb);font-size:14px;color:var(--ink);transition:border-color .2s,box-shadow .2s;outline:none}
#lp-root .lp-input::placeholder{color:var(--faint)}
#lp-root .lp-input:focus{border-color:var(--accent);box-shadow:0 0 0 4px rgba(99,102,241,.14)}
#lp-root .wl-btn{grid-column:1/-1}
#lp-root .wl-note{text-align:center;font-family:var(--fm);font-size:10.5px;letter-spacing:.1em;color:var(--faint);margin-top:18px}
#lp-root .wl-success{max-width:520px;margin:48px auto 0;background:var(--paper);border:1px solid var(--line);border-radius:var(--r);padding:32px;text-align:center}
#lp-root .wl-success .ok{width:46px;height:46px;border-radius:50%;background:var(--soft);color:var(--accent);display:grid;place-items:center;margin:0 auto 16px}
#lp-root .wl-success b{font-size:16px;color:var(--ink)}
#lp-root .wl-success p{font-size:13.5px;color:var(--muted);margin-top:8px}

#lp-root footer{background:#0e0f1c;color:#9a9db2;padding:64px 0 36px}
#lp-root[data-theme="dark"] footer{background:#09091a;border-top:1px solid #1a1b2e}
#lp-root .ft-top{display:flex;justify-content:space-between;align-items:center;gap:26px;flex-wrap:wrap;padding-bottom:42px;border-bottom:1px solid rgba(255,255,255,.08)}
#lp-root .f-brand .brand-name{color:#fff}
#lp-root .f-brand .brand-mark{box-shadow:none}
#lp-root .f-nav{display:flex;gap:26px;flex-wrap:wrap}
#lp-root .f-nav a{font-size:13.5px;transition:color .2s}
#lp-root .f-nav a:hover{color:#fff}
#lp-root .ft-bottom{display:flex;justify-content:space-between;gap:20px;flex-wrap:wrap;padding-top:28px;font-family:var(--fm);font-size:11px;color:#666a80}
#lp-root .f-soc{display:flex;gap:20px}
#lp-root .f-soc a{transition:color .2s}
#lp-root .f-soc a:hover{color:#fff}

@media(max-width:1020px){
  #lp-root .fgrid{grid-template-columns:repeat(2,1fr)}
  #lp-root .tgrid{grid-template-columns:repeat(2,1fr)}
  #lp-root .app-body{grid-template-columns:170px 1fr}
  #lp-root .side.right{display:none}
}
@media(max-width:920px){
  #lp-root .nav,#lp-root .link-login{display:none}
  #lp-root .burger{display:flex}
  #lp-root .rgrid{grid-template-columns:1fr;max-width:560px;margin:0 auto}
  #lp-root .pgrid{grid-template-columns:1fr;max-width:460px;margin:64px auto 0}
  #lp-root .pcard.pro{transform:none}
  #lp-root .steps{grid-template-columns:1fr;gap:38px}
  #lp-root .steps::before{display:none}
}
@media(max-width:860px){
  #lp-root .stats{grid-template-columns:repeat(2,1fr)}
  #lp-root .stat:nth-child(3){border-left:0}
  #lp-root .stat:nth-child(n+3){border-top:1px solid var(--line)}
  #lp-root .app-body{grid-template-columns:1fr}
  #lp-root .side.left{display:none}
  #lp-root .section{padding:80px 0}
  #lp-root .hero{padding:110px 0 70px}
}
@media(max-width:560px){
  #lp-root .fgrid,#lp-root .tgrid{grid-template-columns:1fr}
  #lp-root .wl-form{grid-template-columns:1fr}
  #lp-root .header-cta .btn-primary{display:none}
  #lp-root .f-cards{grid-template-columns:1fr 1fr}
  #lp-root .f-card:last-child{display:none}
}
@media(prefers-reduced-motion:reduce){
  #lp-root *{animation:none!important;transition:none!important}
  #lp-root .reveal{opacity:1;transform:none}
}
`

const CHECK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12.5l5 5L20 6.5"/>
  </svg>
)

const ARR = (
  <svg className="arr" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6"/>
  </svg>
)

// ── Main export ───────────────────────────────────────────────────────────────
export function LandingPage() {
  const navigate = useNavigate()
  const [theme, setTheme] = useState<Theme>('light')
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isFromPH, setIsFromPH] = useState(false)
  const [pricingTab, setPricingTab] = useState<'builder' | 'hosting'>('builder')
  const [lpBilling, setLpBilling] = useState<'month' | 'year'>('month')

  useEffect(() => {
    const saved = localStorage.getItem('bst-lp-theme') as Theme | null
    if (saved === 'light' || saved === 'dark') { setTheme(saved); return }
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) setTheme('dark')
  }, [])

  useEffect(() => {
    // GA4 page view
    const g = (window as unknown as Record<string, unknown>).gtag as ((...a: unknown[]) => void) | undefined
    if (g) g('config', (window as unknown as Record<string, unknown>).__GA_ID, { page_path: '/' })
    // Product Hunt referrer detection
    const params = new URLSearchParams(window.location.search)
    if (params.get('ref') === 'producthunt' || document.referrer.includes('producthunt.com')) {
      setIsFromPH(true)
      track('ph_visit')
    }
    // Restore scroll on mount (editor might have hidden it)
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.documentElement.style.scrollBehavior = 'smooth'
    document.documentElement.style.scrollPaddingTop = '68px'
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
      document.documentElement.style.scrollBehavior = ''
      document.documentElement.style.scrollPaddingTop = ''
    }
  }, [])

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', h, { passive: true })
    return () => window.removeEventListener('scroll', h)
  }, [])

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const els = document.querySelectorAll('#lp-root .reveal')
    if (reduced) { els.forEach(el => el.classList.add('in')); return }
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    els.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [pricingTab])

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const counters = document.querySelectorAll('#lp-root [data-count]')
    if (reduced) {
      counters.forEach(el => { const e = el as HTMLElement; e.textContent = e.dataset.count + (e.dataset.suffix || '') })
      return
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (!en.isIntersecting) return
        const el = en.target as HTMLElement
        const target = parseInt(el.dataset.count!)
        const suffix = el.dataset.suffix || ''
        let t0: number | null = null
        const step = (t: number) => {
          if (!t0) t0 = t
          const p = Math.min(1, (t - t0) / 1400)
          el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suffix
          if (p < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
        io.unobserve(el)
      })
    }, { threshold: 0.6 })
    counters.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  const toggleTheme = (e: MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2
    document.documentElement.style.setProperty('--bst-tx', `${x}px`)
    document.documentElement.style.setProperty('--bst-ty', `${y}px`)
    const apply = () => setTheme(t => {
      const next: Theme = t === 'light' ? 'dark' : 'light'
      localStorage.setItem('bst-lp-theme', next)
      return next
    })
    if (!document.startViewTransition) { apply(); return }
    document.startViewTransition(() => { flushSync(apply) })
  }

  // ── GA4 ──────────────────────────────────────────────────────────────────────
  const track = (event: string, params?: Record<string, string | number>) => {
    const g = (window as unknown as Record<string, unknown>).gtag as ((...a: unknown[]) => void) | undefined
    if (g) g('event', event, params)
  }

  const go = () => navigate('/login?tab=register')
  const goCta = (label: string) => () => { track('cta_click', { label }); navigate('/login?tab=register') }
  const close = () => setMobileOpen(false)

  const JSONLD = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BST',
    applicationCategory: 'WebApplication',
    operatingSystem: 'Web',
    url: 'https://mybst.ru',
    description: 'Опишите бизнес — AI создаст готовый сайт за пару минут. Визуальный конструктор с AI-генерацией, drag&drop и публикацией одной кнопкой.',
    offers: [
      { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'RUB' },
      { '@type': 'Offer', name: 'Personal', price: '790', priceCurrency: 'RUB', billingIncrement: 'P1M' },
      { '@type': 'Offer', name: 'Business', price: '1990', priceCurrency: 'RUB', billingIncrement: 'P1M' },
      { '@type': 'Offer', name: 'Agency', price: '4990', priceCurrency: 'RUB', billingIncrement: 'P1M' },
    ],
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '47' },
    featureList: '376+ blocks, drag-and-drop, AI assistant, e-commerce, CMS, multilingual',
    screenshot: 'https://mybst.ru/api/og',
  }

  return (
    <div id="lp-root" data-theme={theme}>
      <style>{LP_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(JSONLD) }}
      />

      {/* ── Product Hunt welcome banner ── */}
      {isFromPH && (
        <div style={{ background: 'linear-gradient(90deg,#da552f,#f97316)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
            🎉 Привет, охотник! Рады видеть тебя из Product Hunt.
          </span>
          <button
            onClick={goCta('ph_banner')}
            style={{ background: '#fff', color: '#da552f', border: 'none', borderRadius: '8px', padding: '6px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
            Попробовать бесплатно →
          </button>
        </div>
      )}

      {/* ── HEADER ── */}
      <header className={scrolled ? 'scrolled' : ''}>
        <div className="container header-in">
          <a className="brand" href="#top">
            <span className="brand-mark">B</span>
            <span className="brand-name">BST</span>
            <span className="brand-beta">beta</span>
          </a>
          <nav className="nav">
            <a href="#features">Возможности</a>
            <a href="#pricing">Тарифы</a>
            <a href="#vitrina">Стили</a>
            <a href="#early">Начать</a>
          </nav>
          <div className="header-cta">
            <button className="link-login" onClick={go}>Войти</button>
            <button className="btn btn-primary btn-sm" onClick={go}>
              Начать бесплатно {ARR}
            </button>
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light'
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              }
            </button>
            <button className="burger" onClick={() => setMobileOpen(o => !o)} aria-label="Меню">
              <span/><span/>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="mobile-menu">
            <a href="#features" onClick={close}>Возможности</a>
            <a href="#pricing" onClick={close}>Тарифы</a>
            <a href="#vitrina" onClick={close}>Стили</a>
            <a href="#early" onClick={close}>Начать</a>
            <button className="link-login" onClick={() => { close(); go() }}>Войти</button>
            <button className="btn btn-primary btn-block" onClick={() => { close(); go() }}>Начать бесплатно →</button>
          </div>
        )}
      </header>

      <main id="top">

        {/* ── HERO ── */}
        <section className="hero">
          <div className="container">
            <div className="hero-head">
              <span className="badge reveal"><span className="dot"/>AI · Открытая бета</span>
              <h1 className="reveal" style={{ '--d': '.06s' } as React.CSSProperties}>
                Опишите бизнес —<br/><span className="acc">AI создаст сайт за пару минут.</span>
              </h1>
              <p className="lead reveal" style={{ '--d': '.12s' } as React.CSSProperties}>
                Просто опишите свой бизнес. AI сгенерирует готовый сайт с текстами, секциями и дизайном — и передаст его вам для редактирования.
              </p>
              <div className="hero-cta reveal" style={{ '--d': '.18s' } as React.CSSProperties}>
                <button className="btn btn-primary" onClick={goCta('hero_primary')}>
                  Создать сайт бесплатно
                  <svg className="arr" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </button>
                <a className="btn btn-ghost" href="#features">
                  <span className="play-ic"><svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5l12 7-12 7z"/></svg></span>
                  Смотреть возможности
                </a>
              </div>
              <p className="micro reveal" style={{ '--d': '.24s' } as React.CSSProperties}>Без карты · Без кода · Первый сайт бесплатно</p>
            </div>

            {/* Editor mockup */}
            <div className="window reveal" style={{ '--d': '.2s' } as React.CSSProperties}>
              <div className="win-bar">
                <span className="wdot r"/><span className="wdot y"/><span className="wdot g"/>
                <span className="win-url">mybst.ru</span>
                <span style={{ width: 30 }}/>
              </div>
              <div className="app-bar">
                <span className="app-logo">B</span>
                <span className="app-name">BST</span>
                <span className="app-beta">beta</span>
                <div className="app-tabs">
                  <span className="atab active">Редактор</span>
                  <span className="atab">Страницы</span>
                  <span className="atab">CMS</span>
                </div>
                <div className="app-right">
                  <span className="zoom">75%</span>
                  <span className="mini-btn">Preview</span>
                  <span className="mini-btn primary">Publish</span>
                </div>
              </div>
              <div className="app-body">
                <aside className="side left">
                  <div className="side-tabs">
                    <span className="stab active">Блоки</span>
                    <span className="stab">Слои</span>
                  </div>
                  <div className="side-label">Текст</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6V4h16v2M12 4v16M9 20h6"/></svg>Заголовок</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 11h16M4 16h10"/></svg>Параграф</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="8" width="16" height="8" rx="4"/></svg>Кнопка</div>
                  <div className="side-label">Секции</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>Меню</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/></svg>Features</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12a8 8 0 0 0-8-8H5a2 2 0 0 0-2 2v12l3-3h7a8 8 0 0 0 8-3z"/></svg>Отзывы</div>
                  <div className="b-item"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 20v-6M10 20V5M16 20v-9M20 20H2"/></svg>Цены</div>
                </aside>
                <div className="canvas">
                  <div className="frame">
                    <span className="frame-tag">Hero — выбран</span>
                    <span className="handle tl"/><span className="handle tr"/>
                    <span className="handle bl"/><span className="handle br"/>
                    <div className="f-hero">
                      <span className="f-pill">New · Open Beta</span>
                      <b>Build websites that<br/>actually convert.</b>
                      <i>The fastest visual builder for modern teams.</i>
                      <div className="f-btns">
                        <span className="f-btn a">Get started free</span>
                        <span className="f-btn g">Watch demo ▸</span>
                      </div>
                    </div>
                    <div className="f-cards">
                      <div className="f-card"><span className="f-dot" style={{ background: 'var(--accent)' }}/><b>Fast</b><i>Sub-second load times</i></div>
                      <div className="f-card"><span className="f-dot" style={{ background: '#9b9df6' }}/><b>Beautiful</b><i>Pixel-perfect on every device</i></div>
                      <div className="f-card"><span className="f-dot" style={{ background: '#cdd0fb' }}/><b>Responsive</b><i>Mobile-first by default</i></div>
                    </div>
                  </div>
                </div>
                <aside className="side right">
                  <div className="side-label">Properties</div>
                  <div className="prop"><div className="side-label">Title</div><div className="field">Build websites that…</div></div>
                  <div className="prop"><div className="side-label">Subtitle</div><div className="field">The fastest visual…</div></div>
                  <div className="prop"><div className="side-label">Button text</div><div className="field">Get started free</div></div>
                  <div className="prop"><div className="side-label">Background</div><div className="field bg-row"><span className="swatch"/>gradient</div></div>
                  <div className="prop">
                    <div className="side-label">Align</div>
                    <div className="align-seg">
                      <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h10M4 18h14"/></svg></span>
                      <span className="active"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M7 12h10M5 18h14"/></svg></span>
                      <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M10 12h10M6 18h14"/></svg></span>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ── */}
        <div className="stats-band">
          <div className="container stats">
            <div className="stat reveal"><b className="stat-num" data-count="376" data-suffix="+">376+</b><span className="stat-lbl">готовых блоков</span></div>
            <div className="stat reveal" style={{ '--d': '.08s' } as React.CSSProperties}><b className="stat-num">AI</b><span className="stat-lbl">генерация за минуты</span></div>
            <div className="stat reveal" style={{ '--d': '.16s' } as React.CSSProperties}><b className="stat-num" data-count="15">15</b><span className="stat-lbl">языков перевода</span></div>
            <div className="stat reveal" style={{ '--d': '.24s' } as React.CSSProperties}><b className="stat-num" data-count="90" data-suffix="+">90+</b><span className="stat-lbl">Lighthouse score</span></div>
          </div>
        </div>

        {/* ── FEATURES ── */}
        <section className="section" id="features">
          <div className="container">
            <div className="sec-head">
              <span className="kicker">01 · Возможности</span>
              <h2 className="title">Всё для серьёзного сайта.</h2>
              <p className="sub">Без разработчиков, без компромиссов. Просто откройте редактор.</p>
            </div>
            <div className="fgrid">
              {[
                { n:'01', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="3.5" y="3.5" width="7" height="7" rx="1.6"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.6"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.6"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.6"/></svg>, title: '376+ готовых блоков', desc: 'Drag & drop блоки: слайдеры, квизы, калькуляторы, карты, таймеры, анимации, параллакс — и многое другое. Всё перетаскивается на холст.' },
                { n:'02', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.9L18.8 9.8l-4.9 1.9L12 16.6l-1.9-4.9L5.2 9.8l4.9-1.9z"/><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z"/></svg>, title: 'AI-ассистент', desc: 'Создайте весь сайт по одной фразе. AI-копирайтинг для каждого блока. Автоматические переводы на 15 языков. SEO-метатеги за секунду.' },
                { n:'03', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.4 2.5 3.7 5.4 3.7 8.5s-1.3 6-3.7 8.5c-2.4-2.5-3.7-5.4-3.7-8.5s1.3-6 3.7-8.5z"/></svg>, title: 'Мультиязычность', desc: 'Переводите сайт на 15 языков одной кнопкой. Встроенный языковой переключатель в виде флагов, кнопок или выпадающего меню.' },
                { n:'04', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="9" cy="8" r="3.4"/><path d="M3.5 19.5c.6-3.2 2.8-5.2 5.5-5.2s4.9 2 5.5 5.2"/><circle cx="17" cy="9" r="2.6"/><path d="M16.2 14.6c2.3.4 3.9 2.1 4.4 4.6"/></svg>, title: 'Командная работа', desc: 'Приглашайте коллег по ссылке. Роли Редактор и Просмотр. Real-time совместное редактирование и комментарии прямо на холсте.' },
                { n:'05', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="9.5" cy="20" r="1.4"/><circle cx="17.5" cy="20" r="1.4"/><path d="M3 4h2.6l2.4 12h10l2.5-8H6.2"/></svg>, title: 'Полноценный магазин', desc: 'Каталог, корзина, заказы, промокоды, зоны доставки, цифровые товары. Экспорт в WooCommerce и 1С. Оплата через ЮКассу.' },
                { n:'06', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><path d="M5 20v-7M10.5 20V5M16 20v-9M21 20H3"/></svg>, title: 'CMS и аналитика', desc: 'Headless CMS с API, динамические страницы на основе коллекций, A/B тесты, тепловые карты кликов. SEO-аудит и Core Web Vitals.' },
                { n:'07', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"><path d="M13 2.5L5 13.5h6l-1 8 8-11h-6l1-8z"/></svg>, title: 'Быстрая загрузка', desc: 'Каждый опубликованный сайт проходит автооптимизацию: lazy load, WebP, CSS критический путь. Целимся в Lighthouse 90+ на телефоне.' },
                { n:'08', ico: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><path d="M10.2 13.8a4.2 4.2 0 0 1 0-6l3-3a4.2 4.2 0 0 1 6 6l-1.5 1.5"/><path d="M13.8 10.2a4.2 4.2 0 0 1 0 6l-3 3a4.2 4.2 0 0 1-6-6l1.5-1.5"/></svg>, title: 'Все интеграции', desc: 'AmoCRM, Bitrix24, Mailchimp, Telegram, Webhook, Zapier/Make, JivoChat, WhatsApp, VK, Instagram, Calendly и десятки других.' },
              ].map(({ n, ico, title, desc }, i) => (
                <div className="fcell reveal" key={n} style={{ '--d': `${(i % 4) * 0.06}s` } as React.CSSProperties}>
                  <div className="f-top"><span className="f-ico">{ico}</span><span className="f-num">{n}</span></div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── MARQUEE ── */}
        <div className="marquee" aria-hidden="true">
          <div className="mq-track">
            {[0, 1].map(k => (
              <div className="mq-group" key={k}>
                {['AmoCRM','Bitrix24','Mailchimp','Telegram','Webhook','Zapier / Make','JivoChat','WhatsApp','VK','Instagram','Calendly','ЮКасса','WooCommerce','1C'].map(s => (
                  <span className="mq-item" key={s}>{s}</span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── HOW ── */}
        <section className="section tinted" id="how">
          <div className="container">
            <div className="sec-head" style={{ marginBottom: 0 }}>
              <span className="kicker">02 · Как это работает</span>
              <h2 className="title">Три шага до готового сайта.</h2>
            </div>
            <div className="steps">
              {[
                { num: '01', title: 'Опишите ваш бизнес', desc: 'Расскажите AI в двух словах: тип бизнеса, аудитория, цель сайта — на русском.', d: '0s' },
                { num: '02', title: 'AI строит сайт за пару минут', desc: 'Получите готовый сайт с текстами, секциями и дизайном прямо в редакторе.', d: '.1s' },
                { num: '03', title: 'Поправьте и опубликуйте', desc: 'Скорректируйте детали и нажмите Publish — сайт уходит в эфир с CDN и SSL.', d: '.2s' },
              ].map(({ num, title, desc, d }) => (
                <div className="step reveal" key={num} style={{ '--d': d } as React.CSSProperties}>
                  <span className="step-num">{num}</span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── VITRINA ── */}
        <section className="section" id="vitrina">
          <div className="container">
            <div className="sec-head">
              <span className="kicker">03 · Стили</span>
              <h2 className="title">Один шаблон — четыре характера.</h2>
              <p className="sub">Выберите цветовую тему при создании сайта. AI подберёт тексты и акценты под вашу аудиторию.</p>
            </div>
            {(() => {
              const BentoCard = ({ accent, textColor }: { accent: string; textColor: string }) => (
                <div style={{ padding: '14px 12px' }}>
                  <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                    <span style={{ width: 24, height: 24, borderRadius: 6, background: accent, display: 'block', flexShrink: 0 }}/>
                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center' }}>
                      <i style={{ display: 'block', height: 4, borderRadius: 3, background: `${textColor}28`, width: '58%' }}/>
                      <i style={{ display: 'block', height: 3, borderRadius: 3, background: `${textColor}14`, width: '38%' }}/>
                    </span>
                    <span style={{ width: 44, height: 18, borderRadius: 5, background: accent, display: 'block', alignSelf: 'center', flexShrink: 0 }}/>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 5 }}>
                    <div style={{ borderRadius: 7, padding: '9px 8px', height: 56, background: `${textColor}08`, border: `1px solid ${textColor}10` }}>
                      <i style={{ display: 'block', height: 4, borderRadius: 3, background: accent, width: '75%', marginBottom: 5 }}/>
                      <i style={{ display: 'block', height: 3, borderRadius: 3, background: `${textColor}22`, width: '55%', marginBottom: 3 }}/>
                      <i style={{ display: 'block', height: 3, borderRadius: 3, background: `${textColor}14`, width: '40%' }}/>
                    </div>
                    <div style={{ borderRadius: 7, padding: '9px 8px', height: 56, background: accent }}>
                      <i style={{ display: 'block', height: 4, borderRadius: 3, background: 'rgba(255,255,255,0.9)', width: '70%', marginBottom: 5 }}/>
                      <i style={{ display: 'block', height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.55)', width: '50%', marginBottom: 3 }}/>
                      <i style={{ display: 'block', height: 14, borderRadius: 4, background: 'rgba(255,255,255,0.25)', width: '100%', marginTop: 3 }}/>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 5 }}>
                    <div style={{ borderRadius: 7, padding: '8px', height: 32, background: `${textColor}06`, border: `1px solid ${textColor}0d` }}>
                      <i style={{ display: 'block', height: 3, borderRadius: 3, background: `${textColor}18`, width: '60%' }}/>
                    </div>
                    <div style={{ borderRadius: 7, height: 32, background: `${textColor}06`, border: `1px solid ${textColor}0d` }}/>
                  </div>
                </div>
              )
              const VARIANTS = [
                { id: 'noir',    name: 'Noir',    tag: 'Премиум · Агентство',   d: '0s',   bg: '#090910', accent: '#6366f1', tc: '#ffffff' },
                { id: 'atelier', name: 'Atelier', tag: 'Редакция · Бутик',      d: '.1s',  bg: '#f5f0e8', accent: '#c9973d', tc: '#1c1208' },
                { id: 'roast',   name: 'Roast',   tag: 'Кофейня · Ресторан',    d: '.2s',  bg: '#1c100a', accent: '#c07843', tc: '#f5e4ca' },
                { id: 'volt',    name: 'Volt',    tag: 'Технологии · Спорт',    d: '.3s',  bg: '#0c0c10', accent: '#d4f200', tc: '#f0f0f0' },
              ]
              const Arrow = () => <svg className="t-arrow" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              return (
                <div className="tgrid">
                  {VARIANTS.map(v => (
                    <button key={v.id} className="tcard reveal" style={{ '--d': v.d, '--accent': v.accent } as React.CSSProperties} onClick={goCta(`vitrina_${v.id}`)}>
                      <div className="thumb" style={{ background: v.bg, border: 'none', padding: 0 }}>
                        <BentoCard accent={v.accent} textColor={v.tc} />
                      </div>
                      <div className="t-info">
                        <span className="t-name">{v.name}</span>
                        <span className="t-tag">{v.tag}</span>
                        <Arrow />
                      </div>
                    </button>
                  ))}
                </div>
              )
            })()}
            <div className="t-more reveal">
              <button className="btn btn-primary" onClick={goCta('vitrina_cta')}>
                Создать сайт в своём стиле
                <svg className="arr" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section className="section" id="pricing">
          <div className="container">
            <div className="sec-head" style={{ marginBottom: 0 }}>
              <span className="kicker">04 · Тарифы</span>
              <h2 className="title">Прозрачные цены.</h2>
              <p className="sub">Начните бесплатно. Платите когда готовы расти.</p>
            </div>

            {/* Tab switcher */}
            <div className="ptabs">
              <button className={`ptab-btn${pricingTab === 'builder' ? ' active' : ''}`} onClick={() => setPricingTab('builder')}>
                Конструктор
              </button>
              <button className={`ptab-btn${pricingTab === 'hosting' ? ' active' : ''}`} onClick={() => setPricingTab('hosting')}>
                Облачный сервис
              </button>
            </div>

            {/* Billing toggle */}
            <div className="billing-toggle">
              <div className="btoggle">
                <button className={`btoggle-btn${lpBilling === 'month' ? ' active' : ''}`} onClick={() => setLpBilling('month')}>Месяц</button>
                <button className={`btoggle-btn${lpBilling === 'year' ? ' active' : ''}`} onClick={() => setLpBilling('year')}>Год</button>
              </div>
              {lpBilling === 'year' && <span className="save-badge">−25% · 2 месяца бесплатно</span>}
            </div>

            {/* Builder plans */}
            {pricingTab === 'builder' && (
              <div className="pgrid-4">
                <div className="pcard reveal">
                  <span className="p-name">Free</span>
                  <div className="p-price">0 ₽</div>
                  <p className="p-desc">Для старта и экспериментов.</p>
                  <ul className="p-list">
                    {['14 дней бесплатно','1 сайт · поддомен .mybst.ru','500 МБ хранилища','20+ готовых блоков','SSL сертификат'].map(f => (
                      <li key={f}>{CHECK}{f}</li>
                    ))}
                  </ul>
                  <button className="btn btn-outline-w p-cta" onClick={go}>Начать бесплатно</button>
                </div>
                <div className="pcard pro reveal" style={{ '--d': '.07s' } as React.CSSProperties}>
                  <span className="p-badge">Популярный</span>
                  <span className="p-name">Personal</span>
                  <div className="p-price">
                    {lpBilling === 'month' ? '790' : '590'} ₽ <span className="per">/мес</span>
                  </div>
                  {lpBilling === 'year' && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginBottom: 4 }}>7 080 ₽/год</div>}
                  <p className="p-desc">Для профессионалов и фрилансеров.</p>
                  <ul className="p-list">
                    {['5 сайтов','Свой домен + SSL','5 ГБ хранилища','Все блоки без ограничений','Формы и заявки','Приоритетная поддержка'].map(f => (
                      <li key={f}>{CHECK}{f}</li>
                    ))}
                  </ul>
                  <button className="btn btn-white p-cta" onClick={go}>
                    Начать за {lpBilling === 'month' ? '790' : '590'} ₽/мес
                  </button>
                </div>
                <div className="pcard reveal" style={{ '--d': '.14s' } as React.CSSProperties}>
                  <span className="p-name">Business</span>
                  <div className="p-price">
                    {lpBilling === 'month' ? '1 990' : '1 490'} ₽ <span className="per">/мес</span>
                  </div>
                  {lpBilling === 'year' && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>17 880 ₽/год</div>}
                  <p className="p-desc">Для студий и малого бизнеса.</p>
                  <ul className="p-list">
                    {['15 сайтов','Свой домен + SSL','15 ГБ хранилища','E-commerce модуль','Командная работа','CMS с API'].map(f => (
                      <li key={f}>{CHECK}{f}</li>
                    ))}
                  </ul>
                  <button className="btn btn-outline-w p-cta" onClick={go}>
                    Начать за {lpBilling === 'month' ? '1 990' : '1 490'} ₽/мес
                  </button>
                </div>
                <div className="pcard reveal" style={{ '--d': '.21s' } as React.CSSProperties}>
                  <span className="p-name">Agency</span>
                  <div className="p-price">
                    {lpBilling === 'month' ? '4 990' : '3 990'} ₽ <span className="per">/мес</span>
                  </div>
                  {lpBilling === 'year' && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>47 880 ₽/год</div>}
                  <p className="p-desc">Для агентств и команд.</p>
                  <ul className="p-list">
                    {['Неограниченные сайты','White Label','CMS с API','Аналитика и воронки','Управление командой','Приоритетная поддержка'].map(f => (
                      <li key={f}>{CHECK}{f}</li>
                    ))}
                  </ul>
                  <button className="btn btn-outline-w p-cta" onClick={go}>Связаться с нами</button>
                </div>
              </div>
            )}

            {/* Cloud plans */}
            {pricingTab === 'hosting' && (
              <>
                <div className="pgrid-4">
                  {([
                    {
                      name: 'Старт',  monthly: 149,  annual: 112,  color: '#06b6d4',
                      cpu: '1 vCPU',  ram: '512 МБ', nvme: '10 ГБ', bandwidth: '100 Мбит/с',
                      desc: 'Для старта.',
                      features: ['SSL автоматически','Nginx','Ежедневные бэкапы'],
                      featured: false, badge: '',
                    },
                    {
                      name: 'Бизнес', monthly: 299,  annual: 224,  color: '#0ea5e9',
                      cpu: '1 vCPU',  ram: '1 ГБ',   nvme: '20 ГБ', bandwidth: '200 Мбит/с',
                      desc: 'Для растущих проектов.',
                      features: ['SSL автоматически','Nginx · PHP 8.3','Бэкапы','Мониторинг'],
                      featured: false, badge: '',
                    },
                    {
                      name: 'Про',    monthly: 599,  annual: 449,  color: '#6366f1',
                      cpu: '2 vCPU', ram: '2 ГБ',   nvme: '40 ГБ', bandwidth: '500 Мбит/с',
                      desc: 'Для высокой нагрузки.',
                      features: ['SSL + wildcard','CDN-кэширование','DDoS-защита L3/L4','Поддержка 24/7'],
                      featured: true, badge: 'Популярный',
                    },
                    {
                      name: 'Макс',   monthly: 1399, annual: 1049, color: '#8b5cf6',
                      cpu: '4 vCPU', ram: '8 ГБ',   nvme: '80 ГБ', bandwidth: '1 Гбит/с',
                      desc: 'Максимальная мощность.',
                      features: ['SSL Pro · wildcard CDN','DDoS Pro L7','SLA 99.9%','Выделенный IP'],
                      featured: false, badge: '',
                    },
                  ]).map((p, i) => {
                    const price = lpBilling === 'month' ? p.monthly : p.annual
                    const yearTotal = (p.annual * 12).toLocaleString('ru')
                    const specBg = p.featured ? 'rgba(255,255,255,0.12)' : '#f1f5f9'
                    const specColor = p.featured ? '#fff' : '#111'
                    const specMuted = p.featured ? 'rgba(255,255,255,0.55)' : '#64748b'
                    return (
                      <div
                        key={p.name}
                        className={`pcard${p.featured ? ' host-featured' : ''} reveal`}
                        style={{ '--d': `${i * 0.07}s` } as React.CSSProperties}
                      >
                        {p.badge && <span className="p-badge">{p.badge}</span>}
                        <span className="p-name">{p.name}</span>

                        {/* CPU / RAM / NVMe grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2px', borderRadius: '10px', overflow: 'hidden', margin: '10px 0 6px' }}>
                          {[{ v: p.cpu, l: 'CPU' }, { v: p.ram, l: 'RAM' }, { v: p.nvme, l: 'NVMe' }].map(s => (
                            <div key={s.l} style={{ background: specBg, padding: '8px 4px', textAlign: 'center' }}>
                              <div style={{ fontWeight: 900, fontSize: '12px', color: specColor }}>{s.v}</div>
                              <div style={{ fontSize: '9px', fontWeight: 700, marginTop: '2px', color: specMuted, letterSpacing: '0.5px' }}>{s.l}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: '11px', textAlign: 'center', marginBottom: '10px', padding: '4px 0', borderRadius: '6px', background: specBg, color: specMuted, fontWeight: 600 }}>
                          Канал {p.bandwidth}
                        </div>

                        <div className="p-price">{price} ₽ <span className="per">/мес</span></div>
                        {lpBilling === 'year' && (
                          <div style={{ fontSize: 11, color: p.featured ? 'rgba(255,255,255,.6)' : 'var(--muted)', marginBottom: 4 }}>
                            {yearTotal} ₽/год
                          </div>
                        )}
                        <p className="p-desc">{p.desc}</p>
                        <ul className="p-list">
                          {p.features.map(f => <li key={f}>{CHECK}{f}</li>)}
                        </ul>
                        <button
                          className={`btn ${p.featured ? 'btn-white' : 'btn-outline-w'} p-cta`}
                          onClick={go}
                        >
                          {`Начать за ${price} ₽/мес`}
                        </button>
                      </div>
                    )
                  })}
                </div>
                <p className="p-note reveal">
                  Все планы включают SSL-сертификаты и автоматические бэкапы. NVMe SSD. Отмена в любой момент.
                  <br />
                  <span style={{ fontSize: 11 }}>
                    Сравнение: Timeweb Cloud от 199 ₽ за 1 vCPU/1 ГБ/15 ГБ — наши цены выгоднее при тех же ресурсах.
                  </span>
                </p>
              </>
            )}

            {pricingTab === 'builder' && (
              <p className="p-note reveal">Все тарифы включают SSL, CDN и хостинг на наших серверах. Оплата разовая · без автосписания.</p>
            )}
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section className="section early" id="early">
          <div className="container">
            <div className="sec-head" style={{ marginBottom: 0 }}>
              <span className="kicker">05 · Начать</span>
              <h2 className="title">Начните прямо сейчас.</h2>
              <p className="sub">BST в открытой бете — регистрация бесплатная. 5 AI-генераций в подарок при первом входе.</p>
            </div>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <button className="btn btn-primary reveal" onClick={goCta('final_cta')} style={{ fontSize: 16, padding: '16px 36px', borderRadius: 14 }}>
                Создать сайт бесплатно
                <svg className="arr" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
              <p className="micro reveal" style={{ '--d': '.1s', marginTop: 20 } as React.CSSProperties}>Без карты · Без кода · Без лимитов на блоки</p>
            </div>
          </div>
        </section>

      </main>

      {/* ── FOOTER ── */}
      <footer>
        <div className="container">
          <div className="ft-top">
            <a className="brand f-brand" href="#top">
              <span className="brand-mark">B</span>
              <span className="brand-name">BST</span>
              <span className="brand-beta">beta</span>
            </a>
            <nav className="f-nav">
              <a href="#features">Возможности</a>
              <a href="#pricing">Тарифы</a>
              <a href="#vitrina">Стили</a>
              <a href="#early">Начать</a>
            </nav>
          </div>
          <div className="ft-bottom">
            <span>© {new Date().getFullYear()} BST — визуальный конструктор сайтов.</span>
            <div className="f-soc">
              <a href="/privacy">Политика конфиденциальности</a>
              <a href="/terms">Пользовательское соглашение</a>
              <a href="/cookies">Cookie</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
